import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { downloadFile, FileType } from 'commons/utils/file'
import { convertBooleanApiValue, decodeArrayBufferToString } from 'commons/utils/string'
import { t } from 'i18next'
import { toast } from 'react-toastify'
import {
  downloadFairShareBMU,
  downloadFairShareOffice,
  FairShareOpenOfficeWallet,
  getFairShareBMU,
  getFairShareBMUByMaterial,
  getFairShareOffice,
  getFairShareOfficeByMaterial,
  getFairShareTracking,
  getMaterialShares,
  postEditMaterialLevel,
  uploadFairShareBMU,
  uploadFairShareMeta,
  uploadFairShareOffice,
} from 'services/fairShare'
import type {
  FairShareBMUByMaterialPayload,
  FairShareBMUPayload,
  FairShareBMUResponse,
  FairShareOfficeByMaterialPayload,
  FairShareOfficePayload,
  FairShareOfficeResponse,
  FairShareOpenWalletPayload,
  FairShareTrackingPayload,
  FairShareTrackingResponse,
  MaterialShareResponse,
} from 'services/fairShare/interfaces'
import type { AppThunk } from 'store/store'

type InitialStateType = {
  //BMU
  loadingBMUView: boolean
  loadingBMUDownload: boolean
  sharesBMU: FairShareBMUResponse
  bmuFilters: FairShareBMUPayload | null
  //Office
  loadingOfficeView: boolean
  loadingOfficeDownload: boolean
  loadingOpenWallets: boolean
  sharesOffice: FairShareOfficeResponse
  officeFilters: FairShareOfficePayload | null
  //Shared
  openUploadShareModal: boolean
  loadingShareUpload: boolean
  openUploadMetaModal: boolean
  loadingMetaUpload: boolean
  loadingTableItem: boolean
  hasOptionalFilters: boolean
  pagination: {
    currentPage: number
    pageSize: number
  }
  // Material
  loadingMaterialShare: boolean
  loadingLevelUpdate: boolean
  materialShares: MaterialShareResponse
  // Tracking
  loadingTracking: boolean
  sharesTracking: FairShareTrackingResponse
  trackingPagination: {
    currentPage: number
    pageSize: number
    totalItems: number
  }
}

const initialState: InitialStateType = {
  bmuFilters: null,
  hasOptionalFilters: false,
  loadingBMUDownload: false,
  loadingBMUView: true,
  loadingLevelUpdate: false,
  loadingMaterialShare: false,
  loadingMetaUpload: false,
  loadingOfficeDownload: false,
  loadingOfficeView: true,
  loadingOpenWallets: false,
  loadingShareUpload: false,
  loadingTableItem: false,
  loadingTracking: false,
  materialShares: {
    colors: [],
  },
  officeFilters: null,
  openUploadMetaModal: false,
  openUploadShareModal: false,
  pagination: {
    currentPage: 1,
    pageSize: 20,
  },
  sharesBMU: {
    businessUnits: [],
    data: [],
    totals: {
      availableBalance: 0,
      consideredQuantity: 0,
      rowsCount: 0,
      share: 0,
      totalApprovedQuantity: 0,
      totalCancelledQuantity: 0,
      totalSalesPlanQuantity: 0,
      totalTargetQuantity: 0,
      totalTypedQuantity: 0,
    },
  },
  sharesOffice: {
    data: [],
    offices: [],
    totals: {
      availableBalance: 0,
      consideredQuantity: 0,
      rowsCount: 0,
      share: 0,
      totalApprovedQuantity: 0,
      totalCancelledQuantity: 0,
      totalSalesPlanQuantity: 0,
      totalTargetQuantity: 0,
      totalTypedQuantity: 0,
    },
  },
  sharesTracking: {
    data: [],
    rowsCount: 0,
  },
  trackingPagination: {
    currentPage: 1,
    pageSize: 20,
    totalItems: 0,
  },
}

const fairShare = createSlice({
  initialState,
  name: 'fairShare',
  reducers: {
    resetShared(state) {
      state.hasOptionalFilters = false
      state.loadingTableItem = false
      state.pagination.currentPage = 1
      state.pagination.pageSize = 20
      state.loadingTracking = false
      state.sharesTracking = { data: [], rowsCount: 0 }
      state.trackingPagination = { currentPage: 1, pageSize: 20, totalItems: 0 }
    },
    resetTracking(state) {
      state.loadingTracking = false
      state.sharesTracking = { data: [], rowsCount: 0 }
      state.trackingPagination = { currentPage: 1, pageSize: 20, totalItems: 0 }
    },
    setBMUFilters(state, action: PayloadAction<FairShareBMUPayload>) {
      state.bmuFilters = action.payload
    },
    setHasOptionalFilters(state, action: PayloadAction<boolean>) {
      state.hasOptionalFilters = action.payload
    },
    setLoadingBMUDownload(state, action: PayloadAction<boolean>) {
      state.loadingBMUDownload = action.payload
    },
    setLoadingBMUView(state, action: PayloadAction<boolean>) {
      state.loadingBMUView = action.payload
    },
    setLoadingLevelUpdate(state, action: PayloadAction<boolean>) {
      state.loadingLevelUpdate = action.payload
    },
    setLoadingMaterialShare(state, action: PayloadAction<boolean>) {
      state.loadingMaterialShare = action.payload
    },
    setLoadingMetaUpload(state, action: PayloadAction<boolean>) {
      state.loadingMetaUpload = action.payload
    },
    setLoadingOfficeDownload(state, action: PayloadAction<boolean>) {
      state.loadingOfficeDownload = action.payload
    },
    setLoadingOfficeView(state, action: PayloadAction<boolean>) {
      state.loadingOfficeView = action.payload
    },
    setLoadingOpenWallets(state, action: PayloadAction<boolean>) {
      state.loadingOpenWallets = action.payload
    },
    setLoadingShareUpload(state, action: PayloadAction<boolean>) {
      state.loadingShareUpload = action.payload
    },
    setLoadingTableItem(state, action: PayloadAction<boolean>) {
      state.loadingTableItem = action.payload
    },
    setLoadingTracking(state, action: PayloadAction<boolean>) {
      state.loadingTracking = action.payload
    },
    setMaterialShares(state, action: PayloadAction<MaterialShareResponse>) {
      state.materialShares = action.payload
    },
    setOfficeFilters(state, action: PayloadAction<FairShareOfficePayload>) {
      state.officeFilters = action.payload
    },
    setOpenUploadMetaModal(state, action: PayloadAction<boolean>) {
      state.openUploadMetaModal = action.payload
    },
    setOpenUploadShareModal(state, action: PayloadAction<boolean>) {
      state.openUploadShareModal = action.payload
    },
    setPage(state, action: PayloadAction<number>) {
      state.pagination.currentPage = action.payload
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.pagination.pageSize = action.payload
      state.pagination.currentPage = 1
    },
    setSharesBMU(state, action: PayloadAction<FairShareBMUResponse>) {
      state.sharesBMU = action.payload
    },
    setSharesBMUData(state, action: PayloadAction<FairShareBMUResponse['data']>) {
      state.sharesBMU.data = action.payload
    },
    setSharesOffice(state, action: PayloadAction<FairShareOfficeResponse>) {
      state.sharesOffice = action.payload
    },
    setSharesOfficeData(state, action: PayloadAction<FairShareOfficeResponse['data']>) {
      state.sharesOffice.data = action.payload
    },
    setSharesTracking(state, action: PayloadAction<FairShareTrackingResponse>) {
      state.sharesTracking = action.payload
    },
    setTrackingPage(state, action: PayloadAction<number>) {
      state.trackingPagination.currentPage = action.payload
    },
    setTrackingPageSize(state, action: PayloadAction<number>) {
      state.trackingPagination.pageSize = action.payload
      state.trackingPagination.currentPage = 1
    },
    setTrackingTotalItems(state, action: PayloadAction<number>) {
      state.trackingPagination.totalItems = action.payload
    },
  },
})

export const {
  setLoadingBMUView,
  setLoadingBMUDownload,
  setLoadingShareUpload,
  setSharesBMU,
  setSharesBMUData,
  setLoadingTableItem,
  setHasOptionalFilters,
  setPage,
  setPageSize,
  setBMUFilters,
  setLoadingOfficeView,
  setLoadingOfficeDownload,
  setSharesOffice,
  setSharesOfficeData,
  setOfficeFilters,
  setLoadingOpenWallets,
  setLoadingMaterialShare,
  setMaterialShares,
  setLoadingLevelUpdate,
  resetShared,
  resetTracking,
  setOpenUploadShareModal,
  setOpenUploadMetaModal,
  setLoadingMetaUpload,
  setLoadingTracking,
  setSharesTracking,
  setTrackingPage,
  setTrackingPageSize,
  setTrackingTotalItems,
} = fairShare.actions

export const fairShareContext = fairShare.reducer

export const fetchSharesBMU =
  (payload: FairShareBMUPayload): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingBMUView(true))
    dispatch(setHasOptionalFilters(false))
    dispatch(setBMUFilters(payload))
    try {
      const response = await getFairShareBMU(payload)
      if (payload.centerCode || payload.colorCodes.length || payload.materialCodes.length) {
        dispatch(setHasOptionalFilters(true))
      }
      dispatch(setSharesBMU(response))
    } catch (error) {
      toast.error(t('common.errors.unableToFetchData'))
    } finally {
      dispatch(setLoadingBMUView(false))
    }
  }

export const fetchBMUMaterialColors =
  (payload: FairShareBMUByMaterialPayload): AppThunk =>
  async (dispatch, getState) => {
    dispatch(setLoadingTableItem(true))
    try {
      const shareData = getState().fairShare.sharesBMU.data
      const response = await getFairShareBMUByMaterial(payload)
      const newMaterialList = shareData.map((item) => {
        if (item.code === payload.materialCode && item.center === payload.centerCode) {
          return {
            ...item,
            colors: response,
            expanded: true,
          }
        }
        return item
      })
      dispatch(setSharesBMUData(newMaterialList))
    } catch (error) {
      toast.error(t('common.errors.unableToFetchData'))
    } finally {
      dispatch(setLoadingTableItem(false))
    }
  }

export const downloadSharesBMU = (): AppThunk => async (dispatch, getState) => {
  const bmuFilters = getState().fairShare.bmuFilters
  if (!bmuFilters) return

  dispatch(setLoadingBMUDownload(true))
  try {
    toast.info(t('common.messages.downloadStarting'))
    const response = await downloadFairShareBMU(bmuFilters)
    downloadFile({
      file: response,
      fileName: 'fair_share_bmu.xlsx',
      fileType: FileType.Excel,
    })
    toast.success(t('common.messages.reportDownloadedSuccessfully'))
  } catch (error) {
    toast.error(t('common.errors.somethingWentWrong'))
  } finally {
    dispatch(setLoadingBMUDownload(false))
  }
}

export const updateFairShareBMU =
  (file: FormData, toastCallback: (file: ArrayBuffer) => void): AppThunk =>
  async (dispatch, getState) => {
    dispatch(setLoadingShareUpload(true))
    try {
      const response = await uploadFairShareBMU(file)

      const status = response.status

      if (status === 200) {
        const bmuFilters = getState().fairShare.bmuFilters
        if (bmuFilters) dispatch(fetchSharesBMU(bmuFilters))
        toast.success(t('screens.fairShare.shareLoadedSuccess'))
      }
      if (status === 202) {
        toastCallback(response.data)
      }
    } catch (error) {
      toast.error(t('common.errors.somethingWentWrong'))
    } finally {
      dispatch(setLoadingShareUpload(false))
    }
  }

export const updateFairShareOffice =
  (file: FormData, toastCallback: (file: ArrayBuffer) => void): AppThunk =>
  async (dispatch, getState) => {
    dispatch(setLoadingShareUpload(true))
    try {
      const response = await uploadFairShareOffice(file)

      const status = response.status

      if (status === 200) {
        const officeFilters = getState().fairShare.officeFilters
        if (officeFilters) dispatch(fetchSharesOffice(officeFilters))
        toast.success(t('screens.fairShare.shareLoadedSuccess'))
      }
      if (status === 202) {
        toastCallback(response.data)
      }
    } catch (error) {
      toast.error(t('common.errors.somethingWentWrong'))
    } finally {
      dispatch(setLoadingShareUpload(false))
    }
  }

export const fetchSharesOffice =
  (payload: FairShareOfficePayload): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingOfficeView(true))
    dispatch(setHasOptionalFilters(false))
    dispatch(setOfficeFilters(payload))
    try {
      const response = await getFairShareOffice(payload)
      if (
        payload.teamCode ||
        payload.regionCode ||
        payload.centerCode ||
        payload.colorCodes.length ||
        payload.materialCodes.length
      ) {
        dispatch(setHasOptionalFilters(true))
      }
      dispatch(setSharesOffice(response))
    } catch (error) {
      toast.error(t('common.errors.unableToFetchData'))
    } finally {
      dispatch(setLoadingOfficeView(false))
    }
  }

export const fetchOfficeMaterialColors =
  (payload: FairShareOfficeByMaterialPayload): AppThunk =>
  async (dispatch, getState) => {
    dispatch(setLoadingTableItem(true))
    try {
      const shareData = getState().fairShare.sharesOffice.data
      const response = await getFairShareOfficeByMaterial(payload)
      const newMaterialList = shareData.map((item) => {
        if (item.code === payload.materialCode && item.center === payload.centerCode) {
          return {
            ...item,
            colors: response,
            expanded: true,
          }
        }
        return item
      })
      dispatch(setSharesOfficeData(newMaterialList))
    } catch (error) {
      toast.error(t('common.errors.somethingWentWrong'))
    } finally {
      dispatch(setLoadingTableItem(false))
    }
  }

export const openOfficeWallets =
  (payload: FairShareOpenWalletPayload, toastCallback: (file: ArrayBuffer) => void): AppThunk =>
  async (dispatch, getState) => {
    dispatch(setLoadingOpenWallets(true))
    try {
      const response = await FairShareOpenOfficeWallet(payload)

      if (response.status === 200) {
        toast.success(t('screens.fairShare.walletFromOfficeSuccess'))
        const officeFilters = getState().fairShare.officeFilters
        if (officeFilters) dispatch(fetchSharesOffice(officeFilters))
      }

      if (response.status === 202) {
        toastCallback(response.data)
      }
    } catch (error) {
      toast.error(t('common.errors.somethingWentWrong'))
    } finally {
      dispatch(setLoadingOpenWallets(false))
    }
  }

export const downloadSharesOffice = (): AppThunk => async (dispatch, getState) => {
  const officeFilters = getState().fairShare.officeFilters
  if (!officeFilters) return

  dispatch(setLoadingOfficeDownload(true))
  try {
    toast.info(t('common.messages.downloadStarting'))
    const response = await downloadFairShareOffice(officeFilters)
    downloadFile({
      file: response,
      fileName: 'fair_share_office.xlsx',
      fileType: FileType.Excel,
    })
    toast.success(t('common.messages.reportDownloadedSuccessfully'))
  } catch (error) {
    toast.error(t('common.errors.somethingWentWrong'))
  } finally {
    dispatch(setLoadingOfficeDownload(false))
  }
}

export const fetchMaterialShare =
  (materialCode: string, materialCenter: string): AppThunk =>
  async (dispatch, getState) => {
    dispatch(setLoadingMaterialShare(true))
    try {
      const { draftBody } = getState().salesOrder

      if (convertBooleanApiValue(draftBody.fl_utlz_fair_shar)) {
        const payload = {
          businessUnit: draftBody.cd_unid_negc,
          centerCode: materialCenter,
          channel: draftBody.cd_canl_dstb,
          materialCode: materialCode,
          officeCode: draftBody.cd_escr_vend,
          organization: draftBody.cd_orgn_vend,
          regionCode: draftBody.cd_regi_vend,
          salesOrderType: draftBody.cd_tipo_orde,
          sector: draftBody.cd_seto_atvd,
          teamCode: draftBody.cd_equi_vend,
          yearMonth: Number(draftBody.dt_ano_mes_cota),
        }
        const response = await getMaterialShares(payload)
        dispatch(setMaterialShares(response))
      }
    } catch (error) {
      dispatch(setMaterialShares({ colors: [] }))
      toast.error(t('common.errors.somethingWentWrong'))
    } finally {
      dispatch(setLoadingMaterialShare(false))
    }
  }

export const updateMaterialLevel =
  (selectedLevel: string): AppThunk =>
  async (dispatch, getState) => {
    dispatch(setLoadingLevelUpdate(true))
    try {
      const {
        officeFilters,
        sharesOffice: { data },
      } = getState().fairShare

      const checkedMaterials = data.filter((item) => item.checked)
      const { businessUnit, yearMonth, officeCode, centerCode, teamCode, regionCode } =
        officeFilters as FairShareOfficePayload

      const payload = {
        bmuCode: businessUnit,
        centerCode: !checkedMaterials.length ? centerCode : null,
        level: selectedLevel,
        materials: checkedMaterials.length
          ? checkedMaterials.map((item) => {
              return {
                centerCode: item.center,
                materialCode: item.code,
              }
            })
          : undefined,
        officeCode: officeCode || null,
        regionCode: regionCode || null,
        teamCode: teamCode || null,
        yearMonth: yearMonth,
      }

      const response = await postEditMaterialLevel(payload)
      if (response.status === 200) {
        toast.success(
          t('screens.fairShare.editLevelSuccess', {
            context: checkedMaterials.length ? 'selected' : 'all',
          }),
        )
      }
      dispatch(fetchSharesOffice(officeFilters as FairShareOfficePayload))
    } catch (error) {
      toast.error(t('common.errors.somethingWentWrong'))
    } finally {
      dispatch(setLoadingLevelUpdate(false))
    }
  }

export const updateFairShareMeta =
  (file: FormData, toastCallback: (file: ArrayBuffer) => void): AppThunk =>
  async (dispatch, getState) => {
    dispatch(setLoadingMetaUpload(true))
    try {
      const response = await uploadFairShareMeta(file)

      const status = response.status

      if (status === 200) {
        const officeFilters = getState().fairShare.officeFilters
        if (officeFilters) dispatch(fetchSharesOffice(officeFilters))
        toast.success(t('screens.fairShare.metaLoadedSuccess'))
      }
      if (status === 202) {
        toastCallback(response.data)
      }
      if (status === 400) {
        const text = decodeArrayBufferToString(response.data)
        toast.error(text)
      }
    } catch (error) {
      console.error(error)
      toast.error(t('common.errors.somethingWentWrong'))
    } finally {
      dispatch(setLoadingMetaUpload(false))
    }
  }

export const fetchFairShareTracking =
  (payload: FairShareTrackingPayload): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingTracking(true))
    try {
      const response = await getFairShareTracking(payload)

      if (response.data.length) {
        dispatch(setSharesTracking(response))
        dispatch(setTrackingTotalItems(response.rowsCount))
      } else {
        dispatch(setSharesTracking({ data: [], rowsCount: 0 }))
        dispatch(setTrackingTotalItems(0))
      }
    } catch (_) {
      toast.error(t('common.errors.unableToFetchData'))
    } finally {
      dispatch(setLoadingTracking(false))
    }
  }
